import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import './PushNotifications.css';

const AUDIENCES = [
  {
    value: 'salon_owners',
    label: 'Salon owners only',
    hint: 'Devices registered with role owner (high-priority salon channel).',
  },
  {
    value: 'customers_all',
    label: 'All customers',
    hint: 'Every active customer device token.',
  },
  {
    value: 'customers_never_booked',
    label: 'Customers who never booked',
    hint: 'Customer role and no row in bookings as customerId.',
  },
  {
    value: 'customers_inactive_25d',
    label: 'Customers with no booking in the last 25 days',
    hint: 'Includes customers who never booked, or whose last booking was more than 25 days ago.',
  },
  {
    value: 'test_prasanth_device',
    label: "Test: Prasanth's device only",
    hint:
      'Sends only to deviceTokens for UID TgpHAKghgEes5oK3qk6yzHmblMg1 (uses salon channel if role is owner).',
  },
];

function PushNotifications() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('customers_all');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [lastResult, setLastResult] = useState(null);
  /** Snapshot when user taps Send broadcast; null = modal closed */
  const [confirmDraft, setConfirmDraft] = useState(null);

  const openConfirmModal = () => {
    const t = title.trim();
    const b = body.trim();
    if (!t || !b) {
      setError('Please enter both a title and a message.');
      return;
    }
    setError('');
    const selected = AUDIENCES.find((a) => a.value === audience);
    setConfirmDraft({
      title: t,
      body: b,
      audience,
      audienceLabel: selected?.label || audience,
    });
  };

  const closeConfirmModal = () => {
    if (!sending) setConfirmDraft(null);
  };

  const confirmAndSend = async () => {
    if (!confirmDraft) return;
    const { title: t, body: b, audience: aud } = confirmDraft;

    setLastResult(null);
    setSending(true);

    try {
      const send = httpsCallable(functions, 'sendAdminBroadcast');
      const result = await send({ title: t, body: b, audience: aud });
      setLastResult(result.data);
      setConfirmDraft(null);
    } catch (e) {
      setError(e.message || 'Request failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="push-notifications-page">
      {confirmDraft && (
        <div
          className="push-notifications-modal-backdrop"
          role="presentation"
          onClick={closeConfirmModal}
        >
          <div
            className="push-notifications-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="broadcast-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="broadcast-confirm-title" className="push-notifications-modal-heading">
              Confirm broadcast
            </h3>
            <p className="push-notifications-modal-warning">
              This will send a real push notification. This cannot be undone.
            </p>

            <dl className="push-notifications-modal-summary">
              <div>
                <dt>Audience</dt>
                <dd>{confirmDraft.audienceLabel}</dd>
              </div>
              <div>
                <dt>Title</dt>
                <dd>{confirmDraft.title}</dd>
              </div>
              <div>
                <dt>Message</dt>
                <dd className="push-notifications-modal-message">{confirmDraft.body}</dd>
              </div>
            </dl>

            <div className="push-notifications-modal-actions">
              <button
                type="button"
                className="push-notifications-send-btn push-notifications-send-btn--secondary"
                disabled={sending}
                onClick={closeConfirmModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="push-notifications-send-btn push-notifications-send-btn--primary"
                disabled={sending}
                onClick={confirmAndSend}
              >
                {sending ? 'Sending…' : 'Confirm & send'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <h2>Broadcast push</h2>
        <p>
          Compose a message and choose who receives it. Sends through your Firebase Cloud Function
          (Expo) to <code>deviceTokens</code>. Deploy the <code>sendAdminBroadcast</code> callable
          from the mobile app <code>functions</code> package when you update server logic only —
          not for each message.
        </p>
      </div>

      {error && (
        <div className="push-notifications-banner push-notifications-banner--error" role="alert">
          {error}
        </div>
      )}

      {lastResult && (
        <div className="push-notifications-banner push-notifications-banner--ok">
          <strong>Result</strong>
          <pre className="push-notifications-json">
            {JSON.stringify(lastResult, null, 2)}
          </pre>
        </div>
      )}

      <div className="push-notifications-form-card">
        <div className="push-notifications-field">
          <label htmlFor="broadcast-title">Title</label>
          <input
            id="broadcast-title"
            type="text"
            maxLength={120}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Short title (shown in the notification shade)"
            disabled={sending || !!confirmDraft}
          />
          <span className="push-notifications-char-hint">{title.length}/120</span>
        </div>

        <div className="push-notifications-field">
          <label htmlFor="broadcast-body">Message</label>
          <textarea
            id="broadcast-body"
            rows={6}
            maxLength={2000}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Main message body"
            disabled={sending || !!confirmDraft}
          />
          <span className="push-notifications-char-hint">{body.length}/2000</span>
        </div>

        <fieldset className="push-notifications-fieldset" disabled={sending || !!confirmDraft}>
          <legend>Audience</legend>
          {AUDIENCES.map((a) => (
            <label key={a.value} className="push-notifications-radio-row">
              <input
                type="radio"
                name="audience"
                value={a.value}
                checked={audience === a.value}
                onChange={() => setAudience(a.value)}
              />
              <span className="push-notifications-radio-text">
                <span className="push-notifications-radio-label">{a.label}</span>
                <span className="push-notifications-radio-hint">{a.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <button
          type="button"
          className="push-notifications-send-btn push-notifications-send-btn--primary"
          disabled={sending || !!confirmDraft || !title.trim() || !body.trim()}
          onClick={openConfirmModal}
        >
          Send broadcast
        </button>
      </div>
    </div>
  );
}

export default PushNotifications;
